/**
 * cart-bridge.js — the theme adapter for the `cro:cart:updated` contract.
 *
 * Copy to the theme as `assets/<scope>-cart-bridge.js` and load it as a classic deferred
 * script, AFTER the host theme's own global scripts (on Dawn: after `pubsub.js`/`constants.js`,
 * or `publish` and `PUB_SUB_EVENTS` are not yet defined).
 *
 * Implements all three directions of skills/cro-blocks/references/cart-event-contract.md:
 *   1. CRO block -> theme      (cart icon + drawer refresh)
 *   2. theme -> CRO block      (shipping bar stays correct when the theme edits quantities)
 *   3. drawer injection        (cro-shipping-bar / cro-cart-upsell into the cart drawer)
 *
 * Event payload (contract, fixed):
 *   detail = { source, action: 'add'|'update'|'remove',
 *              lines: [{ id: '<variant id string>', quantity }],
 *              cart: <full /cart.js JSON>, response }
 *
 * Optional configuration on the script tag:
 *   data-drawer-sections="cro-shipping-bar,cro-cart-upsell"   (section ids from templates/cart.json)
 *
 * Theme detection is by feature at event time, not at load time: Dawn and Horizon both register
 * their custom elements from deferred/module scripts, so a load-time check races them and loses.
 *
 * Verified against Horizon @ 8b42ace57642e45a3a59841d2ae06d386c929e72 and
 * Dawn @ 258f00f64365e2018ca4c62778a6bf55a5d3cd18. No dependencies, no build step.
 */
(function () {
  'use strict';

  var EVENT_NAME = 'cro:cart:updated';
  var THEME_SOURCE = 'theme-adapter';
  var HORIZON_EVENT = 'shopify:cart:lines-update';
  var STANDARD_EVENTS_URL = 'https://cdn.shopify.com/storefront/standard-events.js';
  var COUNT_SELECTORS = [
    '[data-cart-count]',
    '[data-cart-item-count]',
    '.cart-count-bubble span[aria-hidden="true"]',
    '#cart-icon-bubble .cart-count-bubble span:first-child',
    '.cart-count',
    '.header__cart-count',
  ];

  var script = document.currentScript;
  var drawerSections = ((script && script.dataset.drawerSections) || '')
    .split(',')
    .map(function (id) {
      return id.trim();
    })
    .filter(Boolean);

  var standardEventsPromise = null;
  var outbound = 0; // direction-1 events in flight; direction 2 ignores those.

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function fetchCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  /** Resolves the @shopify/events module, or null when it cannot be loaded. */
  function standardEvents() {
    if (window.StandardEvents) return Promise.resolve(window.StandardEvents);
    if (!standardEventsPromise) {
      standardEventsPromise = import(STANDARD_EVENTS_URL).catch(function () {
        return null;
      });
    }
    return standardEventsPromise;
  }

  function detectTheme() {
    if (
      customElements.get('cart-icon') ||
      customElements.get('cart-drawer-component') ||
      document.querySelector('cart-icon, theme-drawer#cart-drawer')
    ) {
      return 'horizon';
    }
    if (
      customElements.get('cart-drawer') ||
      customElements.get('cart-notification') ||
      (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined')
    ) {
      return 'dawn';
    }
    return 'generic';
  }

  function count(detail) {
    return (detail.cart && detail.cart.item_count) || 0;
  }

  /* ---------------------------------------------------------------- generic */

  function updateCounts(n) {
    var hit = 0;
    COUNT_SELECTORS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (node) {
        node.textContent = String(n);
        node.hidden = false;
        hit += 1;
      });
    });
    return hit > 0;
  }

  function toast(message) {
    var el = document.createElement('div');
    el.className = 'cro-cart-toast';
    el.setAttribute('role', 'status');
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(function () {
      el.remove();
    }, 4000);
  }

  function genericRefresh(detail) {
    if (!updateCounts(count(detail))) toast('Cart updated (' + count(detail) + ')');
  }

  /* ---------------------------------------- direction 1: Horizon -------- */

  function horizonRefresh(detail) {
    var n = count(detail);
    // Horizon's ADD path uses `merchandiseId` (raw variant id); only the cart-items
    // change path uses `id`, and there it is a cart line key, not a variant id.
    var lines = (detail.lines || []).map(function (line) {
      return detail.action === 'add'
        ? { merchandiseId: String(line.id), quantity: line.quantity }
        : { id: String(line.id), quantity: line.quantity };
    });

    return standardEvents().then(function (events) {
      var Ctor = events && events.CartLinesUpdateEvent;
      var summary = null;
      if (Ctor && typeof Ctor.createCartFromAjaxResponse === 'function' && detail.cart && detail.cart.currency) {
        try {
          summary = Ctor.createCartFromAjaxResponse(detail.cart);
        } catch (error) {
          summary = null;
        }
      }
      // cart-icon.js reads `cart.totalQuantity ?? detail.itemCount` — both are set.
      if (!summary) summary = { id: (detail.cart && detail.cart.token) || '', totalQuantity: n, lines: [], discountCodes: [] };

      var result = {
        cart: summary,
        detail: {
          itemCount: n,
          items: (detail.cart && detail.cart.items) || undefined,
          source: detail.source,
          didError: false,
        },
      };
      var payload = { action: detail.action, context: 'cro-blocks', lines: lines };
      var deferred = Ctor && typeof Ctor.createPromise === 'function' ? Ctor.createPromise() : null;
      var event = null;

      if (Ctor && deferred) {
        payload.promise = deferred.promise;
        try {
          event = new Ctor(payload);
        } catch (error) {
          event = null;
        }
      }
      if (!event) {
        // Synthesised fallback: cart-icon.js, cart-drawer.js and component-cart-items.js read
        // `action`, `promise` and `detail` straight off the event object. `promise` is
        // MANDATORY — without it cart-icon.js updates nothing at all.
        event = new Event(HORIZON_EVENT, { bubbles: true });
        event.action = payload.action;
        event.context = payload.context;
        event.lines = lines;
        event.detail = result.detail;
        event.promise = Promise.resolve(result);
      }

      outbound += 1;
      document.dispatchEvent(event);
      if (deferred) deferred.resolve(result);
      setTimeout(function () {
        outbound -= 1;
      }, 0);
    });
  }

  /* ------------------------------------------- direction 1: Dawn -------- */

  function fetchSections(ids) {
    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) {
        return r.json();
      })
      .catch(function () {
        return null;
      });
  }

  function dawnRefresh(detail) {
    var host = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    var n = count(detail);
    var pending = Promise.resolve(null);

    if (host && typeof host.getSectionsToRender === 'function') {
      pending = fetchSections(
        host.getSectionsToRender().map(function (section) {
          return section.id;
        })
      );
    }

    return pending.then(function (sections) {
      var painted = false;
      if (host && sections) {
        // renderContents reads `parsedState.key` first (cart-notification keys its per-line
        // selector off it); the add response is the only place that key exists.
        var state = { sections: sections };
        if (detail.response && detail.response.key) state.key = detail.response.key;
        if (detail.response && detail.response.id) state.id = detail.response.id;
        try {
          host.renderContents(state);
          painted = true;
        } catch (error) {
          painted = false;
        }
      }
      if (!painted) updateCounts(n);

      // pubsub.js declares `publish`/`subscribe` as top-level functions in a CLASSIC deferred
      // script, so they are reachable from another classic script — no module import needed.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        outbound += 1;
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: detail.source,
          productVariantId: detail.lines && detail.lines[0] && detail.lines[0].id,
          cartData: detail.cart,
        });
        setTimeout(function () {
          outbound -= 1;
        }, 0);
      }
    });
  }

  /* ------------------------------------ direction 2: theme -> blocks ---- */

  function rebroadcast(action, lines) {
    if (outbound > 0) return; // our own direction-1 dispatch, not a theme-originated change
    return fetchCart().then(function (cart) {
      document.dispatchEvent(
        new CustomEvent(EVENT_NAME, {
          bubbles: true,
          detail: { source: THEME_SOURCE, action: action || 'update', lines: lines || [], cart: cart, response: null },
        })
      );
    });
  }

  function wireDirectionTwo() {
    document.addEventListener(HORIZON_EVENT, function (event) {
      var lines = (event.lines || []).map(function (line) {
        return { id: String(line.merchandiseId || line.id || ''), quantity: line.quantity };
      });
      var done = event.promise && typeof event.promise.then === 'function' ? event.promise : Promise.resolve();
      done
        .then(function () {
          return rebroadcast(event.action, lines);
        })
        .catch(function () {});
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
        var id = data && data.productVariantId;
        rebroadcast('update', id ? [{ id: String(id), quantity: 0 }] : []);
      });
    }
  }

  /* ------------------------------- direction 3: drawer injection -------- */

  function drawerRoot() {
    return (
      document.querySelector('theme-drawer#cart-drawer cart-drawer-component') ||
      document.querySelector('cart-drawer .drawer__inner') ||
      document.querySelector('cart-drawer')
    );
  }

  function injectDrawerSections() {
    if (!drawerSections.length) return;
    var target = drawerRoot();
    if (!target) return;
    var missing = drawerSections.filter(function (id) {
      return !target.querySelector('[data-cro-injected="' + id + '"]');
    });
    if (!missing.length) return;

    fetchSections(missing).then(function (sections) {
      if (!sections) return;
      missing.forEach(function (id) {
        var html = sections[id];
        if (!html) return;
        var wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        var node = wrapper.querySelector('[data-section]') || wrapper.firstElementChild;
        if (!node) return;
        node.setAttribute('data-cro-injected', id);
        target.appendChild(node);
      });
    });
  }

  function watchDrawer() {
    if (!drawerSections.length) return;
    injectDrawerSections();
    // The theme replaces the drawer's inner HTML on every update — re-inject after it does.
    new MutationObserver(function () {
      injectDrawerSections();
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* --------------------------------------------------------------- wiring */

  document.addEventListener(EVENT_NAME, function (event) {
    var detail = event.detail || {};
    if (detail.source === THEME_SOURCE) return; // loop guard: direction 2 never re-enters direction 1
    if (!detail.cart) return; // contract: detail.cart is always the full /cart.js body

    function fallback() {
      genericRefresh(detail);
    }

    var theme = detectTheme();
    try {
      if (theme === 'horizon') horizonRefresh(detail).catch(fallback);
      else if (theme === 'dawn') dawnRefresh(detail).catch(fallback);
      else genericRefresh(detail);
    } catch (error) {
      fallback();
    }
  });

  wireDirectionTwo();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawer, { once: true });
  } else {
    watchDrawer();
  }
})();
